<script setup lang="ts">
import { dollars, from } from '@nuts/auto-delivery-sdk/dist/utils/money';
import { useIntersectionObserver } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { computed, onMounted, reactive, ref } from 'vue';
import { useStore } from 'vuex';

import { reportEngagement } from '@/api/dynamic-yield/engagement';
import { productsByKeys } from '@/api/productsByKeys';
import AddToCartOrCustomize from '@/components/base/add-to-cart/AddToCartOrCustomize.vue';
import GreatestHitsBadge from '@/components/base/GreatestHitsBadge.vue';
import OrderCountBadge from '@/components/base/OrderCountBadge.vue';
import RouteLink from '@/components/base/RouteLink.vue';
import TagBadge from '@/components/base/TagBadge.vue';
import Header6 from '@/components/base/typography/Header6.vue';
import SmallBodyText from '@/components/base/typography/SmallBodyText.vue';
import VariantSizeSelectorModal from '@/components/ppa/VariantSizeSelectorModal.vue';
import BundleDiscountBadge from '@/components/product-card/BundleDiscountBadge.vue';
import PriceStrikethrough from '@/components/product-card/PriceStrikethrough.vue';
import ProductImage from '@/components/product-card/ProductImage.vue';
import ProductName from '@/components/product-card/ProductName.vue';
import Ratings from '@/components/product-card/Ratings.vue';
import { useCallback } from '@/composables/useCallback';
import { useCart } from '@/composables/useCart';
import { useFeatureFlags } from '@/composables/useFeatureFlags';
import { BaseChoice, RecommendationsSlot } from '@/lib/personalization/dynamicYield';
import { isPpaBagSize } from '@/lib/product/ppa';
import { GREATEST_HITS_TAG_NAME } from '@/lib/product/tags';
import { useAutoDelivery } from '@/stores/autoDelivery';
import analytics, { getListMetadata, gtag, ListMetadata, setListMetadata } from '@/utils/analytics';
import { trackFlagExperiment } from '@/utils/analytics/experimentViewedEvent';
import { ListSourceAttribution } from '@/utils/analytics/listSourceAttribution';
import {
  formatNutsAddToCartItemFromProductCard,
  NutsAddToCartItem,
  sendProductAddedEvent,
} from '@/utils/analytics/productAddedEvent';
import {
  isProductClickedPageSection,
  ProductClickedPageSection,
  sendProductClickedEvent,
} from '@/utils/analytics/productClickedEvent';
import {
  formatGTagViewItemListPayload,
  isListPageSection,
  NutsViewItemListItem,
  ProductListViewedEventData,
  pushProductListViewed,
} from '@/utils/analytics/productListViewedEvent';
import { getCartId } from '@/utils/cart';
import { isDefined } from '@/utils/isDefined';
import { toLegacySku } from '@/utils/product';
import { ProductCard, ProductCardData } from '@/utils/productCard';
import { NutsProductProjection } from '@/utils/productProjection';
import { reportError } from '@/utils/reportError';

export type Layout = 'plp' | 'recommendations';

const props = withDefaults(
  defineProps<{
    analyticsList?: string;
    applySubstitutions?: boolean;
    clickTrackable?: boolean;
    compareSkus?: boolean;
    decisionId?: BaseChoice['decisionId'];
    disabledAddToCartButton?: boolean;
    enableAddToCart?: boolean;
    enableSocialProof?: boolean;
    impressionTrackable?: boolean;
    indexName?: string;
    layout?: Layout;
    listSourceAttribution?: ListSourceAttribution;
    hideBorder?: boolean;
    pageSection?:
      | ProductClickedPageSection
      | ProductListViewedEventData['pageSection']
      | NutsAddToCartItem['pageSection'];
    position: number;
    product: ProductCardData;
    linkToPdp?: boolean;
    requestUrl?: string;
    showADMessage?: boolean;
    showBundleDiscountBadge?: boolean;
    showPrice?: boolean;
    showReviews?: boolean;
    showVariantName?: boolean;
    slotId?: RecommendationsSlot['slotId'];
    tag?: keyof HTMLElementTagNameMap;
    trackingEventLocation?: string;
  }>(),
  {
    enableAddToCart: true,
    clickTrackable: true,
    layout: 'recommendations',
    impressionTrackable: true,
    linkToPdp: true,
    showADMessage: true,
    showPrice: true,
    showReviews: true,
    tag: 'div',
    trackingEventLocation: '',
  },
);

const emit = defineEmits(['added', 'click']);

const store = useStore();
const { addToCart, cart } = useCart(store);
const { autoDeliveryChannel } = storeToRefs(useAutoDelivery());

const { flags, flagsMetadata, loadDyFlags } = useFeatureFlags();

const existingListMetadata = ref<ListMetadata>();
const selectedItem = computed(() =>
  props.applySubstitutions ? ProductCard.withSubstitutions(props.product) : props.product,
);
const preSubstitutionSku = computed(() =>
  props.applySubstitutions && props.product.substituteVariant ? props.product.sku : undefined,
);

const displayDiscount = computed<boolean>(() => {
  if (selectedItem.value.totalSavings) return true;
  if (!props.compareSkus || !selectedItem.value.displayDiscountPercent) return false;
  return !!(selectedItem.value.bulk || selectedItem.value.wholesale);
});

const comparisonPrice = computed(() => {
  if (selectedItem.value.totalSavings) {
    return selectedItem.value.totalSavings.comparisonPrice;
  }
  return selectedItem.value.displayComparisonPrice;
});

const discountDisplayValue = computed(() => {
  if (selectedItem.value.totalSavings?.discountDisplayValue) {
    return selectedItem.value.totalSavings.discountDisplayValue;
  }

  return selectedItem.value.displayDiscountPercent;
});

const discountType = computed(() =>
  selectedItem.value.totalSavings?.onSale ? 'product' : undefined,
);

const displayShortVariantName = computed(
  () =>
    props.showVariantName ||
    (selectedItem.value.promoteShortVariantName && !selectedItem.value.requiresCustomization) ||
    !!(selectedItem.value.unitName && !selectedItem.value.onePoundBulk),
);

const displayStars = computed(() => typeof selectedItem.value.averageRating === 'number');

const showAutoDeliveryMessage = computed(
  () =>
    props.showADMessage &&
    !selectedItem.value.hidePrice &&
    selectedItem.value.autoDeliveryEligible &&
    !selectedItem.value.wholesale &&
    !selectedItem.value.totalSavings,
);

const productCard = ref<HTMLElement>();

const microData = computed(() => ({
  id: toLegacySku(props.product.sku),
  position: props.position,
  name: props.product.name,
  price: dollars(selectedItem.value.piecePrice),
  list: props.analyticsList ?? props.trackingEventLocation,
}));

const itemListName = computed<string>(() => props.analyticsList || props.trackingEventLocation);
const nutsViewItemListItem = computed<NutsViewItemListItem>(() => ({
  cost:
    selectedItem.value.cost === undefined ? undefined : from(parseFloat(selectedItem.value.cost)),
  coupon: selectedItem.value.totalSavings?.description?.en,
  discount: selectedItem.value.totalSavings?.value || from(0),
  indexName: props.indexName,
  name: props.product.name,
  position: props.position,
  price: selectedItem.value.piecePrice,
  priceBeforeDiscount:
    selectedItem.value.totalSavings?.comparisonPrice ?? selectedItem.value.piecePrice,
  product_id: props.product.productKey,
  quantity: 1,
  reporting_category: props.product.reportingCategory,
  searchQueryID: selectedItem.value.searchQueryId,
  sku: selectedItem.value.sku,
  variant: selectedItem.value.unitName,
  variantLabel: selectedItem.value.variantLabel,
  weight: selectedItem.value.weight,
}));

const listMetadata = computed<ListMetadata>(() => ({
  ...existingListMetadata.value,
  list: itemListName.value,
  indexName: props.indexName,
  position: props.position,
  preSubstitutionSku: preSubstitutionSku.value,
  searchQueryID: props.product.searchQueryId,
}));

const reportClick = () => {
  if (!props.clickTrackable || !props.linkToPdp) return;
  if (props.decisionId && props.slotId) {
    reportEngagement(props.decisionId, props.slotId);
  }

  analytics.sendEvent('productClick', props.trackingEventLocation, {
    ecommerce: {
      click: {
        actionField: {
          list: microData.value.list,
        },
        products: [microData.value],
      },
    },
  });

  setListMetadata(listMetadata.value);

  const gtagItem = formatGTagViewItemListPayload(itemListName.value, {
    ...nutsViewItemListItem.value,
    sku: props.product.sku,
  });
  gtag('event', 'select_item', {
    items: [gtagItem],
  });

  if (!isProductClickedPageSection(props.pageSection)) {
    reportError(`Incorrect pageSection for Product Clicked event: ${props.pageSection}`);
    return;
  }

  sendProductClickedEvent({
    listSourceAttribution: props.listSourceAttribution,
    pageSection: props.pageSection,
    position: props.position,
    productCard: selectedItem.value,
  });
};

const onProductClicked = () => {
  reportClick();
  emit('click');
};

const impressionLogged = ref(false);
const reportImpression = () => {
  if (!props.impressionTrackable || impressionLogged.value) return;

  if (!isListPageSection(props.pageSection)) {
    reportError(`Incorrect pageSection for Product List Viewed event: ${props.pageSection}`);
    return;
  }

  impressionLogged.value = true;
  pushProductListViewed(itemListName.value, nutsViewItemListItem.value, props.pageSection);
};

const variantSizeSelectorModal: {
  isOpen: boolean;
  product?: NutsProductProjection;
} = reactive({
  isOpen: false,
  product: undefined,
});

const loadProductProjection = async () => {
  if (variantSizeSelectorModal.product) return;
  const [productProjection] = await productsByKeys([props.product.productKey]);
  variantSizeSelectorModal.product = productProjection;
};

const handleAddToCart = useCallback(async () => {
  await loadProductProjection();

  const productUsesNewBagSize = variantSizeSelectorModal.product?.variants.some((variant) =>
    isPpaBagSize(variant.bagSize),
  );

  if (
    Array.isArray(variantSizeSelectorModal.product?.variants) &&
    variantSizeSelectorModal.product.variants.length >= 2 &&
    productUsesNewBagSize
  ) {
    await loadDyFlags('[A/B Test] PPA Phase 2', ['pricePackPhase2'], {
      skipExperimentEvent: true,
    });

    trackFlagExperiment(flagsMetadata.pricePackPhase2);

    if (flags.pricePackPhase2) {
      variantSizeSelectorModal.isOpen = true;
      return;
    }
  }

  sendProductAddedEvent(
    formatNutsAddToCartItemFromProductCard(selectedItem.value, {
      cartId: getCartId(cart.value),
      indexName: props.indexName,
      itemListName: itemListName.value,
      listSourceAttribution: props.listSourceAttribution,
      markedAsGift: false,
      pageSection: props.pageSection,
      position: props.position,
      quantity: 1,
    }),
    preSubstitutionSku.value,
  );

  await addToCart(
    {
      cart_sku: {
        list_metadata: JSON.stringify(listMetadata.value),
        quantity: 1,
        sku_external_id: selectedItem.value.sku,
      },
    },
    {
      postAddToCartCallback: () => null,
      skipCartAdded: false,
    },
  );

  emit('added');
});

useIntersectionObserver(productCard, ([{ isIntersecting }]) => {
  if (isIntersecting) {
    reportImpression();
    loadProductProjection();
  }
});

onMounted(() => {
  existingListMetadata.value = getListMetadata();
});
</script>

<template>
  <component
    v-bind="$attrs"
    class="flex overflow-hidden relative z-0 flex-col justify-between w-full bg-white"
    :class="[
      $attrs.class,
      !hideBorder && layout === 'recommendations' ? 'border-stone-200 border border-solid' : '',
      layout === 'recommendations' ? 'recommendations-slide p-2 md:p-3' : 'plp-slide h-full',
      enableSocialProof ? 'rounded' : 'rounded-xl',
    ]"
    itemprop="item"
    data-test="product-card-recommendation"
    :is="tag"
    itemscope
    itemtype="http://schema.org/Product"
    :data-index="position"
    :data-list="analyticsList"
    ref="productCard"
  >
    <meta itemprop="brand" content="Nuts.com" />
    <meta itemprop="productID" :content="product.productKey" />
    <meta itemprop="url" :content="`${requestUrl}#${position}`" />
    <slot name="top">
      <OrderCountBadge
        v-if="selectedItem.orderCount"
        :orderCount="selectedItem.orderCount"
        class="absolute top-2 right-2 z-10"
      />
      <TagBadge
        v-else-if="!isDefined(flags.socialProofMessaging)"
        :tags="selectedItem.totalSavings ? ['badge-onsale'] : selectedItem.keywords ?? []"
        class="absolute top-0 right-0 z-10"
      />
    </slot>
    <div v-if="enableSocialProof">
      <div v-once class="absolute z-10 w-full pointer-events-none" data-tagg-slot />
    </div>
    <div :class="layout === 'recommendations' ? 'contents' : 'flex flex-col h-full'">
      <component
        :is="linkToPdp ? RouteLink : 'div'"
        :to="product.path"
        class="relative mb-2.5"
        @click="onProductClicked"
      >
        <GreatestHitsBadge
          v-if="!$slots.top && selectedItem.keywords?.includes(GREATEST_HITS_TAG_NAME)"
          animation="grow"
          class="absolute bottom-0 left-0"
          :size="[20, 24]"
          text
        />
        <ProductImage :imageUrl="product.imageUrl" :productName="product.name" :size="'large'" />
      </component>
      <component
        :is="linkToPdp ? RouteLink : 'div'"
        :to="product.path"
        class="overflow-hidden flex-initial no-underline hover:no-underline"
        :class="{ 'order-3': layout === 'plp' }"
        @click="onProductClicked"
      >
        <div class="flex flex-col">
          <ProductName
            :class="{ 'hover:underline': linkToPdp }"
            hasSetHeight
            :productName="product.name"
          />
          <span v-if="!selectedItem.inStock" class="sr-only">This product is sold out.</span>
          <meta itemprop="description" :content="product.shortDescription" />
          <slot name="subhead">
            <div v-if="displayStars && showReviews">
              <Ratings
                :averageRating="product.averageRating"
                :totalRatings="product.totalReviews"
              />
              <div
                v-if="product.totalReviews && product.totalReviews > 0"
                itemprop="aggregateRating"
                itemscope
                itemtype="http://schema.org/AggregateRating"
              >
                <meta itemprop="ratingValue" :content="product.averageRating?.toString()" />
                <meta itemprop="bestRating" content="5" />
                <meta itemprop="reviewCount" :content="product.totalReviews?.toString()" />
              </div>
            </div>
          </slot>
        </div>
        <div v-if="showPrice">
          <template v-if="!selectedItem.hidePrice">
            <PriceStrikethrough
              class="mt-2"
              :comparisonPrice
              :discountDisplayValue
              :discountType
              :displayDiscount="!!displayDiscount"
              :onePoundBulk="selectedItem.onePoundBulk"
              :piecePrice="selectedItem.piecePrice"
              :piecePriceRange="selectedItem.piecePriceRange"
            />
            <SmallBodyText v-if="selectedItem.totalSavings?.description" class="mt-2 text-black">
              {{ selectedItem.totalSavings.description.en }}
            </SmallBodyText>
          </template>
          <Header6 v-else class="mt-2">Build your own</Header6>
        </div>
      </component>
      <BundleDiscountBadge
        v-if="showBundleDiscountBadge"
        class="mt-2"
        :class="{ 'order-3': layout === 'plp' }"
        :productKey="selectedItem.productKey"
      />
      <div v-if="layout === 'plp'" class="order-4">
        <div
          v-if="showAutoDeliveryMessage"
          class="flex items-start mt-2"
          data-test="auto-delivery-callout"
        >
          <img
            src="@/assets/auto-delivery/arrows.svg"
            alt="auto delivery icon"
            class="mr-2 md:mt-0.5"
          />
          <SmallBodyText class="text-black">
            {{ `Save ${autoDeliveryChannel.discount}% with Auto-Delivery` }}
          </SmallBodyText>
        </div>
      </div>
      <div class="flex mb-2" :class="{ 'order-5 items-end': layout === 'plp' }">
        <SmallBodyText
          v-if="displayShortVariantName"
          class="mt-2 text-neutral-500"
          data-test="unit-name"
        >
          {{ selectedItem.unitName }}
        </SmallBodyText>
      </div>
      <slot name="button">
        <div
          v-if="enableAddToCart"
          :class="{ 'pt-2 mt-auto': layout === 'recommendations' }"
          data-test="add-to-cart-or-customize"
        >
          <AddToCartOrCustomize
            :class="{ 'order-2 mb-2': layout === 'plp' }"
            :disabled="disabledAddToCartButton"
            fullWidth
            :isLoading="handleAddToCart.isPending"
            :outOfStock="!selectedItem.inStock"
            :path="selectedItem.path"
            :requiresCustomization="selectedItem.requiresCustomization"
            size="small"
            @add-to-cart="handleAddToCart.execute()"
            @customization-started="emit('added')"
          >
            <template #add-to-cart-text>
              <slot name="add-to-cart-text" />
            </template>
          </AddToCartOrCustomize>
        </div>
      </slot>
    </div>
    <meta itemprop="sku" :content="toLegacySku(selectedItem.sku)" />
    <meta itemprop="mpn" :content="toLegacySku(selectedItem.sku)" />
    <div v-once class="acadia-product-card" />
  </component>
  <Teleport v-if="variantSizeSelectorModal.isOpen" to="#teleported">
    <VariantSizeSelectorModal
      v-if="variantSizeSelectorModal.product"
      :isOpen="variantSizeSelectorModal.isOpen"
      :indexName
      :itemListName
      :listMetadata
      :listSourceAttribution
      :pageSection
      :position
      :product="variantSizeSelectorModal.product"
      @handle-close="variantSizeSelectorModal.isOpen = false"
    />
  </Teleport>
</template>

<style lang="scss" scoped>
.recommendations-slide {
  scroll-snap-align: start;
  min-width: 160px;
  max-width: 208px;
  @include respond-min($screen-md-min) {
    min-width: 173px;
  }
  @include respond-min($screen-lg-min) {
    min-width: 198px;
  }
}
.plp-slide {
  scroll-snap-align: start;
  min-width: 150px;
  @include respond-min($screen-md-min) {
    min-width: 183px;
  }
  @include respond-min($screen-lg-min) {
    min-width: 227px;
  }
}
</style>
